/*
 * Model and Collection for standard responses, which are basically extensions of
 * the TopicConversation Model
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "configuration/properties",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicConversation/topicConversationModel",
  "modules/shared/sortableList/sortableListModel",
  "modules/topicsSplit/topic/topicResponse/topicResponseModel",
], function (
  Backbone,
  $,
  _,
  Properties,
  Messenger,
  TopicConversationModel,
  SortableListModel,
  TopicResponseModel,
) {
  "use strict";

  const StandardResponsesListModel = {};

  // noinspection JSUnusedGlobalSymbols
  StandardResponsesListModel.standardResponse = TopicConversationModel.topicConversation.extend({
    idAttribute: "intentId",
    standardResponse: true, //flag to mark this conversation as a standard response
    statusValue: "", //extension for the status name
    fetchSpecial: function () {
      //fetch the standard response and the live response
      this.fetch();
      const kb = Messenger.reqres.request("active:kb:model");
      kb.onSynced(function () {
        const liveResponse = new StandardResponsesListModel.standardLiveResponse({
          intentId: this.id,
        });
        //if response versioning is enabled, fetch the live version
        if (kb.get("responseVersioning")) {
          liveResponse.fetch();
          liveResponse.once(
            "sync",
            function () {
              this.onSynced(function () {
                //now the standard and live responses are loaded
                this.liveResponse = liveResponse;
                this.liveResponse.workingConversation = this;
              }, this);
            },
            this,
          );
        }
      }, this);
    },
    initializeCustom: function () {
      //assign a default live standard response model (used if response versioning is disabled)
      this.liveResponse = new StandardResponsesListModel.standardLiveResponse();
      this.liveResponse.workingConversation = this; //pass the working response
      //create a placeholder decisionCol
      this.decisionCol = new TopicConversationModel.topicConversations();
      //when we change the status to published, set the live conversation to get published
      this.on("change:statusId", this._updateStatusValue, this);
      this.on("sync", this._updateStatusValue, this);
      if (this.get("statusId") > 0) {
        this._updateStatusValue();
      }
    },
    responseTemplates: function (options) {
      if (this.responseTemplatesCol) {
        return this.responseTemplatesCol;
      }
      const subModelOptions = {
        doNotFetch: false,
        modelClass: TopicResponseModel.responseTemplates,
        urlExtension: "/templates",
      };
      Object.assign(subModelOptions, options);
      if (!this.id) {
        subModelOptions.doNotFetch = true;
      }
      this.responseTemplatesCol = this.getSubModel2(this.responseTemplatesCol, subModelOptions);
      return this.responseTemplatesCol;
    },
    saveModel: function (options) {
      //define the options hash if it hasn't been passed or passed with success and error functions
      const modelOptions = this._prepareSaveModelOptions(options);

      //set any associated content values to the responses (make sure all responses have the same content)
      this._applyAssociatedContent();
      //save the standard response
      if (this._allowSave(options)) {
        this.save(null, modelOptions);
      } else if (modelOptions.success) {
        //standard response is clean, but its children (epc and cat codes) may be dirty, so try saving them
        modelOptions.success(this, {}, options);
      }
    },
    saveChildren: function (options) {
      //save the child collections (epc and cat codes).  When that's done, check if we need to publish
      this.saveSubModels({
        success: function (model, response) {
          if (model.liveResponse && model.liveResponse.needToPublish) {
            model.liveResponse._publishConversation(options);
          } else if (options && options.success) {
            options.success(model, response || {}, options);
          } else {
            Messenger.saveAttempted(true);
          }
        },
      });
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/standard_responses/" + this.id;
    },
    _updateStatusValue: function () {
      const allStatuses = Messenger.reqres.request("kb:statuses");
      allStatuses.onSynced(function () {
        const statusArray = allStatuses.get("allTopicStatuses");
        for (let i = 0; i < statusArray.length; i++) {
          if (statusArray[i].statusId === this.get("statusId")) {
            //do not let the change dirty the topic, but do have it fire a change event
            const isDirty = this.dirty;
            this.statusValue = statusArray[i].statusName;
            this.trigger("change");
            this.dirty = isDirty;
            break;
          }
        }
      }, this);
    },
  });

  //Model for representing live versions of the standard response
  //Used to perform the publish requests to the server
  // noinspection JSUnusedGlobalSymbols
  StandardResponsesListModel.standardLiveResponse =
    TopicConversationModel.liveTopicConversation.extend({
      showConversation: false, //parameter for defining whether we show the live conversation or not
      standardResponse: true, //flag to mark this conversation as a standard response
      url: function () {
        const kbId = Messenger.reqres.request("active:kb");
        return Properties.rootURL + "/" + kbId + "/standard_responses/" + this.id + "/live";
      },
      _getPublishUrl: function (publishAll) {
        const kbId = Messenger.reqres.request("active:kb");
        const intentId = this.id;
        if (publishAll) {
          return Properties.rootURL + "/" + kbId + "/standard_responses/" + intentId + "/publish";
        }
        return (
          Properties.rootURL +
          "/" +
          kbId +
          "/standard_responses/" +
          intentId +
          "/publish/responseTemplates"
        );
      },
    });

  StandardResponsesListModel.standardResponseList = Backbone.Collection.extend({
    model: StandardResponsesListModel.standardResponse,
    assignLiveResponses: function (liveResponses) {
      //make sure that the standard response list is synced first
      this.onSynced(function () {
        this.each(function (response) {
          //overwrite default liveResponse model with the real one from the server
          response.liveResponse = liveResponses.findWhere({ intentId: response.get("intentId") });
          response.liveResponse.workingConversation = response; //pass the working response
        }, this);
      }, this);
    },
    //fetch the standard response list and the live response list, then assign the live responses to their
    // corresponding working versions
    fetchSpecial: function () {
      this.fetch();
      const kb = Messenger.reqres.request("active:kb:model");
      kb.onSynced(function () {
        const liveResponses = new StandardResponsesListModel.standardLiveResponseList();
        //if response versioning is enabled, fetch the live versions
        if (kb.get("responseVersioning")) {
          liveResponses.fetch();
          liveResponses.once(
            "sync",
            function () {
              //once we get the live responses, merge them with the working responses (also once synced)
              this.assignLiveResponses(liveResponses);
            },
            this,
          );
        }
      }, this);
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/standard_responses";
    },
  });

  StandardResponsesListModel.standardLiveResponseList = Backbone.Collection.extend({
    model: StandardResponsesListModel.standardLiveResponse,
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/standard_responses/live";
    },
  });

  //Request the list of standard responses
  Messenger.reqres.setHandler(
    "standard:responses",
    function () {
      const standardResponses = new StandardResponsesListModel.standardResponseList();
      standardResponses.fetchSpecial();
      return standardResponses;
    },
    this,
  );

  //Request a specific standard response
  Messenger.reqres.setHandler(
    "standard:response",
    function (transId) {
      const standardResponse = new StandardResponsesListModel.standardResponse({
        intentId: transId,
      });
      standardResponse.fetchSpecial();
      return standardResponse;
    },
    this,
  );

  //Request for the headers of the standard responses list
  //Headers depend on whether response versioning is enabled or not
  Messenger.reqres.setHandler("standard:responses:headers", function () {
    const Header = SortableListModel.headerModel;
    const responseVersioning = Messenger.reqres
      .request("active:kb:model")
      .get("responseVersioning");
    if (responseVersioning) {
      return new Backbone.Collection([
        new Header({ checkbox: true, width: "14px" }),
        new Header({
          headerName: $.t("topics.headers.name"),
          modelAttribute: "description",
          selectable: true,
          textField: true,
        }),
        new Header({
          headerName: $.t("topics.headers.status"),
          modelAttribute: "_thisObject.statusValue",
        }),
      ]);
    }
    //else return
    return new Backbone.Collection([
      new Header({
        headerName: $.t("topics.headers.name"),
        modelAttribute: "description",
        selectable: true,
        textField: true,
      }),
    ]);
  });

  return StandardResponsesListModel;
});
